import React from "react";
import { Link } from "react-router-dom";
import Card from "../components/Card";
import SiteFooter from "../components/SiteFooter";
import SiteTopNav from "../components/SiteTopNav";
import { TOOL_CATALOG } from "../tools/catalog";

const IMAGE_WORKFLOW_SLUGS = [
  "image-extractor",
  "image-compressor",
  "image-converter",
  "image-to-text",
  "color-picker",
  "color-palette-generator",
] as const;

const WORKFLOW_LABELS: Record<(typeof IMAGE_WORKFLOW_SLUGS)[number], string> = {
  "image-extractor": "Find and collect",
  "image-compressor": "Reduce file size",
  "image-converter": "Change format",
  "image-to-text": "Extract copy",
  "color-picker": "Sample colors",
  "color-palette-generator": "Build a palette",
};

const IMAGE_TOOLS = IMAGE_WORKFLOW_SLUGS.map((slug) =>
  TOOL_CATALOG.find((tool) => tool.id === slug),
).filter((tool): tool is NonNullable<typeof tool> => Boolean(tool));

export default function ImageTools() {
  return (
    <div className="min-h-screen bg-[var(--ng-bg)] text-[var(--ng-text)]">
      <SiteTopNav />
      <main className="ng-page-shell space-y-10 py-8 sm:py-12">
        <section className="grid gap-8 border-2 border-[var(--ng-border)] bg-white p-6 sm:p-10 lg:grid-cols-[minmax(0,1.2fr)_minmax(18rem,0.8fr)]">
          <div>
            <div className="ng-eyebrow">Image Tools / Browser Workflow</div>
            <h1 className="mt-4 max-w-[12ch] text-5xl font-black uppercase leading-[0.88] tracking-[-0.07em] text-[var(--ng-primary)] sm:text-7xl">
              Free Online Image Tools
            </h1>
            <p className="mt-5 max-w-2xl text-base font-medium leading-7 text-[var(--ng-text-muted)] sm:text-lg">
              Extract images from a website, compress or convert files, pull text with OCR, sample exact colors, and build a reusable palette without creating an account.
            </p>
          </div>

          <aside className="border-2 border-[var(--ng-border)] bg-[var(--ng-accent-yellow)] p-5">
            <div className="ng-eyebrow">Start Here</div>
            <h2 className="mt-3 text-2xl font-black uppercase tracking-tight">
              Extract Images From a Website
            </h2>
            <p className="mt-3 text-sm font-medium leading-6">
              Paste a public page URL, inspect the discovered assets, filter the useful files, and export the selection.
            </p>
            <Link className="ng-button ng-button-primary mt-5 min-h-11" to="/tools/image-extractor">
              Open Image Extractor
            </Link>
          </aside>
        </section>

        <section aria-labelledby="image-workflow-heading" className="space-y-5">
          <div>
            <div className="ng-eyebrow">One Connected Workflow</div>
            <h2 id="image-workflow-heading" className="mt-2 text-3xl font-black uppercase tracking-[-0.05em] sm:text-4xl">
              Move From Source to Finished Asset
            </h2>
          </div>

          <ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {IMAGE_TOOLS.map((tool, index) => (
              <li key={tool.id}>
                <Card
                  component={Link}
                  padding="none"
                  to={tool.route}
                  className="group grid h-full min-h-[220px] gap-4 p-5 transition-colors hover:bg-[var(--ng-panel)]"
                  sx={{ color: "inherit", textDecoration: "none" }}
                >
                  <div className="flex items-start justify-between gap-4">
                    <span className="ng-eyebrow">Step {index + 1}</span>
                    <span aria-hidden="true" className="text-lg font-black">↗</span>
                  </div>
                  <div className="mt-auto">
                    <p className="text-xs font-black uppercase tracking-[0.12em] text-[var(--ng-primary)]">
                      {WORKFLOW_LABELS[tool.id as keyof typeof WORKFLOW_LABELS]}
                    </p>
                    <h3 className="mt-2 text-2xl font-black tracking-[-0.04em]">{tool.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-[var(--ng-text-muted)]">{tool.desc}</p>
                  </div>
                </Card>
              </li>
            ))}
          </ol>
        </section>

        <section className="grid gap-6 border-t-2 border-[var(--ng-border)] pt-8 lg:grid-cols-2">
          <div>
            <div className="ng-eyebrow">Why This Collection</div>
            <h2 className="mt-2 text-3xl font-black uppercase tracking-[-0.05em]">
              Practical Image Work Without a Design Suite
            </h2>
          </div>
          <div className="space-y-4 text-sm font-medium leading-7 text-[var(--ng-text-muted)] sm:text-base">
            <p>
              These tools cover the jobs that commonly happen before an image reaches a website, document, design system, or content handoff: finding the source asset, reducing its weight, changing its format, recovering its text, and translating its colors into reusable values.
            </p>
            <p>
              Local file tools run in your browser. URL-based extraction may contact the public source page and a documented proxy fallback when browser cross-origin rules block direct access.
            </p>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
